import { Input, Select } from '../../shared/components/FormControls';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import '../../shared/styles/Page.css';
import '../../shared/styles/OrchestratorPlayers.css';
import '../../shared/styles/Orchestrator.css';
import 'react-toastify/dist/ReactToastify.css';

import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../../firebase/firebaseApp';
import { Button } from '../../shared/components/Button';
import BreakScreen from '../../shared/components/BreakScreen';
import {
  getSession,
  submitSessionCommand,
  listenSessionRolls,
} from '../../features/orchestrator/utils/sessions';
import {
  getUserTheme,
  applyThemeToDocument,
} from '../../shared/utils/userTheme';
import { ToastContainer, toast } from 'react-toastify';
import DiceHistoryModal from '../../features/orchestrator/components/modals/DiceHistoryModal';
import GridBoard from '../../features/orchestrator/components/GridBoard';
import ZoomRepeatButton from '../../features/orchestrator/components/ZoomRepeatButton';
import {
  DEFAULT_GRID_COLS,
  DEFAULT_GRID_ROWS,
  getTokenGridPositionFromWorldPoint,
  normalizeGridConfig,
} from '../../features/orchestrator/utils/gridConfig';
import {
  DEFAULT_GRID_VIEW_STATE,
  loadGridViewState,
  normalizeGridViewState,
  saveGridViewState,
} from '../../features/orchestrator/utils/gridViewState';
import WorldMapModal from '../../features/orchestrator/components/modals/WorldMapModal';
import LibraryModal from '../../features/orchestrator/components/modals/LibraryModal';
import GmDiceModal from '../../features/orchestrator/components/modals/GmDiceModal';
import {
  DualityModeButton,
  DualityRollButton,
} from '../../features/orchestrator/components/DualityRollControl';
import TrackerToastContent from '../../features/orchestrator/components/TrackerToastContent';
import {
  STORAGE_KEYS,
  buildDualityCommand,
  resolveDieIcon,
} from './helpers';
import {
  BookOpen,
  ChevronDown, ChevronLeft, ChevronRight, ChevronUp,
  Dice6, Dices, Library, LogOut, Map, Minus, Plus, Ruler,
  MonitorPlay, Gamepad2, ZoomIn, ZoomOut, Hand, Eye, EyeOff, RotateCcw, CircleQuestionMark
} from 'lucide-react';
import dfLogo from '../../shared/assets/logos/dimensionfactory.png';

const EMPTY_SCENE = { id: null, name: '', layers: [], tokens: [], battleEffects: [], effect: null };
const DEFAULT_PLAYER_GRID_CONFIG = {
  cols: DEFAULT_GRID_COLS,
  rows: DEFAULT_GRID_ROWS,
  type: 'square',
};

export default function OrchestratorPlayerPage() {
  const [sessionToken, setSessionToken] = useState('');
  const [connectedToken, setConnectedToken] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [dualityAdv, setDualityAdv] = useState(false);
  const [dualityDis, setDualityDis] = useState(false);
  const [dualityMode, setDualityMode] = useState('standard');
  const [modPlus, setModPlus] = useState('');
  const [modMinus, setModMinus] = useState('');

  const [playerControlsEnabled, setPlayerControlsEnabled] = useState(false);
  const [gameplaySyncEnabled, setGameplaySyncEnabled] = useState(false);
  const [isOnBreak, setIsOnBreak] = useState(false);
  const [sessionScene, setSessionScene] = useState(null);
  const [playerTokens, setPlayerTokens] = useState([]);
  const [selectedTokenId, setSelectedTokenId] = useState(null);
  const [measuringTokenId, setMeasuringTokenId] = useState(null);
  const [playerPings, setPlayerPings] = useState([]);
  const [showTokenNames, setShowTokenNames] = useState(false);
  const [showDiceHistoryModal, setShowDiceHistoryModal] = useState(false);
  const [showBoardDicePanel, setShowBoardDicePanel] = useState(false);
  const [sessionRolls, setSessionRolls] = useState([]);
  // useBoardView: false = classic, true = board. Auto-switches when gameplaySyncEnabled changes.
  const [useBoardView, setUseBoardView] = useState(false);
  const [showPingCanvas, setShowPingCanvas] = useState(false);
  const [showWorldMapModal, setShowWorldMapModal] = useState(false);
  const [showLibraryModal, setShowLibraryModal] = useState(false);
  const [playerWorldMap, setPlayerWorldMap] = useState(null);
  const [playerLibrary, setPlayerLibrary] = useState(null);
  const [libraryInitialBookId, setLibraryInitialBookId] = useState(null);
  const [boardViewState, setBoardViewState] = useState(DEFAULT_GRID_VIEW_STATE);
  const [boardViewSceneId, setBoardViewSceneId] = useState(null);
  const boardZoom = boardViewState.zoom;
  const [isMobileBoard, setIsMobileBoard] = useState(false);

  const [battleEffectShape, setBattleEffectShape] = useState('circle');
  const [battleEffectSize, setBattleEffectSize] = useState(3);
  const [battleEffectColor, setBattleEffectColor] = useState('#ffb38a');
  const [battleEffectOpacity, setBattleEffectOpacity] = useState(0.4);

  const sessionListenerRef = useRef(null);
  const rollListenerRef = useRef(null);
  const seenRollIdsRef = useRef(new Set());
  const lastTokenTapRef = useRef({ tokenId: null, at: 0 });
  const [custom, setCustom] = useState(['', '', '', '', '']);

  const username = useMemo(() => localStorage.getItem('username') || '', []);
  const playerViewRole = username ? `player:${username}` : 'player';
  const displayName = useMemo(() => localStorage.getItem('displayName') || username, [username]);
  const system = useMemo(() => localStorage.getItem('system') || '', []);
  const boardGridConfig = sessionScene?.gridConfig || DEFAULT_PLAYER_GRID_CONFIG;

  const theme = useMemo(() => getUserTheme(username), [username]);

  useEffect(() => {
    applyThemeToDocument(theme);
  }, [theme]);

  // Load persisted state on mount
  useEffect(() => {
    const savedToken = localStorage.getItem(STORAGE_KEYS.sessionToken);
    if (savedToken) setSessionToken(savedToken);

    try {
      const rawDice = localStorage.getItem(STORAGE_KEYS.diceState);
      if (rawDice) {
        const parsed = JSON.parse(rawDice);
        if (typeof parsed === 'object' && parsed) {
          if (Array.isArray(parsed.custom)) setCustom(parsed.custom);
          if (typeof parsed.dualityAdv === 'boolean')
            setDualityAdv(parsed.dualityAdv);
          if (typeof parsed.dualityDis === 'boolean')
            setDualityDis(parsed.dualityDis);
          if (['standard', 'doomed', 'blessed', 'epic'].includes(parsed.dualityMode))
            setDualityMode(parsed.dualityMode);
          if (typeof parsed.modPlus === 'string') setModPlus(parsed.modPlus);
          if (typeof parsed.modMinus === 'string') setModMinus(parsed.modMinus);
        }
      }
    } catch (err) {
      console.error('Failed to load dice state', err);
    }
  }, []);

  useEffect(() => {
    if (!username || system !== 'orchestrator_user') {
      window.location.href = '/';
    }
  }, [system, username]);

  useEffect(() => {
    if (connectedToken) {
      toast.info('Double click to ping', { autoClose: 3000, toastId: 'player-ping-hint' });
    }
  }, [connectedToken]);

  // Persist session token separately so it survives disconnects
  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.sessionToken, sessionToken || '');
  }, [sessionToken]);

  // Persist dice-related state independently of session connection
  useEffect(() => {
    const payload = {
      dualityAdv,
      dualityDis,
      dualityMode,
      modPlus,
      modMinus,
      custom,
    };
    try {
      localStorage.setItem(STORAGE_KEYS.diceState, JSON.stringify(payload));
    } catch (err) {
      console.error('Failed to save dice state', err);
    }
  }, [custom, dualityAdv, dualityDis, dualityMode, modMinus, modPlus]);

  const handleLogout = () => {
    stopSessionListener();
    stopRollListener();
    setConnectedToken(null);
    setSessionToken('');
    setDualityAdv(false);
    setDualityDis(false);
    setDualityMode('standard');
    setModPlus('');
    setModMinus('');
    setCustom(['', '', '', '', '']);
    setPlayerControlsEnabled(false);
    setGameplaySyncEnabled(false);
    setSessionScene(null);
    setPlayerTokens([]);
    setSelectedTokenId(null);
    setMeasuringTokenId(null);
    setPlayerPings([]);
    setShowDiceHistoryModal(false);
    setShowBoardDicePanel(false);
    setSessionRolls([]);
    setUseBoardView(false);
    setShowPingCanvas(false);
    setPlayerWorldMap(null);
    setPlayerLibrary(null);
    localStorage.removeItem(STORAGE_KEYS.diceState);
    localStorage.removeItem('connectedSessionToken');
    localStorage.removeItem('username');
    localStorage.removeItem('displayName');
    localStorage.removeItem('system');
    localStorage.removeItem('campaign');
    window.location.href = '/';
  };

  const connect = async () => {
    setError(null);
    setLoading(true);
    try {
      const record = await getSession(db, sessionToken);
      if (!record || record.active !== true) {
        setConnectedToken(null);
        setError('Session token not found (or inactive).');
        return;
      }
      const normalized = String(record.token || sessionToken)
        .trim()
        .toUpperCase();
      setConnectedToken(normalized);
      localStorage.setItem('connectedSessionToken', normalized);
    } catch (e) {
      console.error(e);
      setError('Failed to connect to session.');
    } finally {
      setLoading(false);
    }
  };

  const sendCommand = async (command) => {
    if (!connectedToken) return;
    const trimmed = String(command ?? '').trim();
    if (!trimmed) return;

    setLoading(true);
    setError(null);
    try {
      await submitSessionCommand(db, connectedToken, {
        username,
        displayName,
        command: trimmed,
      });
    } catch (e) {
      console.error(e);
      setError('Failed to submit roll.');
    } finally {
      setLoading(false);
    }
  };

  const stopSessionListener = () => {
    if (sessionListenerRef.current) {
      sessionListenerRef.current();
      sessionListenerRef.current = null;
    }
  };

  const stopRollListener = () => {
    if (rollListenerRef.current) {
      rollListenerRef.current();
      rollListenerRef.current = null;
    }
  };

  const toggleDuality = (type) => {
    if (type === 'adv') {
      setDualityAdv((prev) => {
        const next = !prev;
        if (next) setDualityDis(false);
        return next;
      });
      return;
    }

    if (type === 'dis') {
      setDualityDis((prev) => {
        const next = !prev;
        if (next) setDualityAdv(false);
        return next;
      });
    }
  };

  useEffect(() => {
    stopSessionListener();
    stopRollListener();
    setPlayerControlsEnabled(false);
    setGameplaySyncEnabled(false);
    setSessionScene(null);
    setPlayerTokens([]);
    setMeasuringTokenId(null);
    setUseBoardView(false);

    if (!connectedToken) return undefined;

    const unsub = onSnapshot(doc(db, 'sessions', connectedToken), (snap) => {
      if (!snap.exists()) {
        setPlayerControlsEnabled(false);
        setGameplaySyncEnabled(false);
        setIsOnBreak(false);
        setSessionScene(null);
        setPlayerTokens([]);
        setSelectedTokenId(null);
        setMeasuringTokenId(null);
        setUseBoardView(false);
        return;
      }

      const data = snap.data() || {};
      const allow = !!data.allowPlayerControls;
      const syncEnabled = !!data.gameplaySyncEnabled;
      setPlayerControlsEnabled(allow);
      setGameplaySyncEnabled(syncEnabled);
      setUseBoardView(syncEnabled);
      setIsOnBreak(!!data.isOnBreak);
      if (Array.isArray(data.pings)) setPlayerPings(data.pings);
      if (typeof data.showNames === 'boolean') setShowTokenNames(data.showNames);

      // Only explicitly shared resources are available to players.
      setPlayerWorldMap(data.worldMapShared === true ? data.worldMap || null : null);
      setPlayerLibrary(data.libraryShared === true ? data.library || null : null);

      if (data.scene && Array.isArray(data.scene.tokens)) {
        // Store the full scene for the board view
        const scene = data.scene;
        const nextScene = {
          id: scene.id || null,
          name: scene.name || '',
          ttrpgSystem: scene.ttrpgSystem || null,
          layers: Array.isArray(scene.layers) ? scene.layers : [],
          tokens: Array.isArray(scene.tokens) ? scene.tokens : [],
          battleEffects: Array.isArray(scene.battleEffects) ? scene.battleEffects : [],
          effect: scene.effect || null,
          gridConfig: normalizeGridConfig(scene.gridConfig),
        };
        setSessionScene((previousScene) => {
          if (previousScene && JSON.stringify(previousScene) === JSON.stringify(nextScene)) {
            return previousScene;
          }
          return nextScene;
        });

        const tokens = scene.tokens
          .filter((token) => token && token.visible !== false)
          .map((token) => ({
            id: token.id,
            name: token.name || 'Token',
          }));
        setPlayerTokens(tokens);
        setSelectedTokenId((prev) =>
          tokens.some((t) => t.id === prev) ? prev : null,
        );
        setMeasuringTokenId((prev) =>
          tokens.some((t) => t.id === prev) ? prev : null,
        );

      } else {
        setSessionScene(null);
        setPlayerTokens([]);
        setSelectedTokenId(null);
        setMeasuringTokenId(null);
      }
    });

    sessionListenerRef.current = unsub;
    return () => {
      stopSessionListener();
    };
  }, [connectedToken]);

  const disconnect = () => {
    stopSessionListener();
    stopRollListener();
    setConnectedToken(null);
    setPlayerControlsEnabled(false);
    setGameplaySyncEnabled(false);
    setSessionScene(null);
    setPlayerTokens([]);
    setSelectedTokenId(null);
    setMeasuringTokenId(null);
    setShowDiceHistoryModal(false);
    setShowBoardDicePanel(false);
    setSessionRolls([]);
    setUseBoardView(false);
    setShowPingCanvas(false);
    setPlayerWorldMap(null);
    setPlayerLibrary(null);
    setShowWorldMapModal(false);
    setShowLibraryModal(false);
  };

  const handleBoardZoomIn = () => {
    setBoardViewState((previous) => normalizeGridViewState({
      ...previous,
      zoom: previous.zoom + 0.2,
    }));
  };

  const handleBoardZoomOut = () => {
    setBoardViewState((previous) => normalizeGridViewState({
      ...previous,
      zoom: previous.zoom - 0.2,
    }));
  };

  const handleBoardZoomReset = () => {
    setBoardViewState(DEFAULT_GRID_VIEW_STATE);
    setMeasuringTokenId(null);
  };

  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 768px)');
    const handleMediaChange = () => setIsMobileBoard(mediaQuery.matches);
    handleMediaChange();
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleMediaChange);
      return () => mediaQuery.removeEventListener('change', handleMediaChange);
    }
    mediaQuery.addListener(handleMediaChange);
    return () => mediaQuery.removeListener(handleMediaChange);
  }, []);

  const sendMoveCommand = (tokenId, dx, dy) => {
    if (!connectedToken || !playerControlsEnabled) return;
    const payload = `MOVE ${tokenId} ${dx} ${dy}`;
    sendCommand(payload);
  };

  const sendMeasureEnableCommand = (tokenId) => {
    if (!connectedToken || !playerControlsEnabled || !tokenId) return;
    setSelectedTokenId(tokenId);
    setMeasuringTokenId(tokenId);
  };

  const sendMeasureDisableCommand = () => {
    if (!connectedToken || !playerControlsEnabled) return;
    setMeasuringTokenId(null);
  };

  const toggleMeasureForToken = (tokenId) => {
    if (!tokenId) return;
    if (measuringTokenId === tokenId) {
      sendMeasureDisableCommand();
      return;
    }
    sendMeasureEnableCommand(tokenId);
  };

  const handleTokenChipTap = (tokenId) => {
    setSelectedTokenId(tokenId);
    if (!playerControlsEnabled) return;

    const now = Date.now();
    const { tokenId: lastTokenId, at: lastTapAt } = lastTokenTapRef.current;
    const isDoubleTap = lastTokenId === tokenId && now - lastTapAt <= 450;

    if (isDoubleTap) {
      lastTokenTapRef.current = { tokenId: null, at: 0 };
      // Double tap should always enable measuring for that token.
      toggleMeasureForToken(tokenId);
      return;
    }

    lastTokenTapRef.current = { tokenId, at: now };
  };

  const sendMoveSelected = (dx, dy) => {
    if (!selectedTokenId) return;
    sendMoveCommand(selectedTokenId, dx, dy);
  };

  const sendBattleEffectCommand = () => {
    if (!selectedTokenId || !connectedToken || !playerControlsEnabled) return;
    const clampedSize = Math.min(10, Math.max(1, Math.round(Number(battleEffectSize) || 3)));
    const payload = `BATTLE_EFFECT ${selectedTokenId} ${battleEffectShape} ${clampedSize} ${battleEffectColor} ${battleEffectOpacity}`;
    sendCommand(payload);
  };

  const sendPingCommand = (x, y) => {
    if (!connectedToken) return;
    const payload = `PING ${x} ${y}`;
    sendCommand(payload);
  };

  const handleBoardPing = (x, y) => {
    if (!playerControlsEnabled || loading) return;
    sendPingCommand(x, y);
  };

  const handlePingCanvasClick = (event) => {
    const canvas = event.currentTarget;
    const rect = canvas.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;

    // Convert pixel coordinates to grid coordinates (24x30 grid)
    const gridX = Math.floor((x / rect.width) * 24);
    const gridY = Math.floor((y / rect.height) * 30);

    // Ensure coordinates are within bounds
    if (gridX >= 0 && gridX < 24 && gridY >= 0 && gridY < 30) {
      sendPingCommand(gridX, gridY);
    }
  };

  // Listen for new dice rolls and toast them on the player screen.
  useEffect(() => {
    stopRollListener();
    seenRollIdsRef.current = new Set();
    setSessionRolls([]);

    if (!connectedToken) return undefined;

    let initialized = false;

    const unsub = listenSessionRolls(db, connectedToken, (docs) => {
      if (!Array.isArray(docs)) return;

      setSessionRolls(docs);

      if (!initialized) {
        docs.forEach((d) => {
          if (d?.id) seenRollIdsRef.current.add(d.id);
        });
        initialized = true;
        return;
      }

      docs.forEach((roll) => {
        if (!roll || !roll.id || seenRollIdsRef.current.has(roll.id)) return;
        seenRollIdsRef.current.add(roll.id);

        const accent = roll.theme?.accent;
        const background = roll.theme?.background;
        const options =
          accent || background
            ? {
                style: {
                  background: background || '#1a1a1a',
                  color: '#fff',
                  border: accent ? `2px solid ${accent}` : undefined,
                },
              }
            : undefined;

        if (roll.kind === 'tracker' && roll.tracker) {
          toast(
            <TrackerToastContent
              tracker={roll.tracker}
              changeInfo={roll.changeInfo || null}
              variant='announcement'
            />,
            options,
          );
          return;
        }

        const message = `${roll.username || 'Player'}: ${roll.displayText || 'rolled.'}`;
        toast(message, options);
      });
    });

    rollListenerRef.current = unsub;
    return () => stopRollListener();
  }, [connectedToken]);

  // Keyboard arrow key support for selecting and nudging tokens
  useEffect(() => {
    const handler = (e) => {
      if (!playerControlsEnabled) return;
      const tag =
        e.target && e.target.tagName
          ? String(e.target.tagName).toUpperCase()
          : '';
      if (tag === 'INPUT' || tag === 'TEXTAREA' || e.target?.isContentEditable)
        return;
      let dx = 0;
      let dy = 0;
      switch (e.key) {
        case 'ArrowUp':
          dy = -1;
          break;
        case 'ArrowDown':
          dy = 1;
          break;
        case 'ArrowLeft':
          dx = -1;
          break;
        case 'ArrowRight':
          dx = 1;
          break;
        default:
          return;
      }
      e.preventDefault();
      // If no token selected, do nothing; selection is via clicking token chips.
      if (!selectedTokenId) return;
      sendMoveSelected(dx, dy);
    };

    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [playerControlsEnabled, selectedTokenId, sendMoveSelected]);

  useEffect(() => {
    if (!measuringTokenId) return;
    if (!playerTokens.some((token) => token.id === measuringTokenId)) {
      setMeasuringTokenId(null);
    }
  }, [measuringTokenId, playerTokens]);

  // Handle token drag on the player's GridBoard – sends SETPOS command to session.
  const handlePlayerDragToken = useCallback(
    (tokenId, worldPoint) => {
      if (!connectedToken || !playerControlsEnabled || !sessionScene) return;
      const token = (sessionScene.tokens || []).find((t) => t.id === tokenId);
      if (!token || !worldPoint) return;
      const tokenSize = typeof token.size === 'string' && token.size.toLowerCase() === 'presenting'
        ? 10
        : Math.min(4, Math.max(1, Number(token.size) || 1));
      const position = getTokenGridPositionFromWorldPoint(worldPoint, tokenSize, boardGridConfig);
      if (!position) return;
      const { x: newX, y: newY } = position;

      if (token.x === newX && token.y === newY) return;
      sendCommand(`SETPOS ${tokenId} ${newX} ${newY}`);
    },
    [boardGridConfig, connectedToken, playerControlsEnabled, sessionScene, sendCommand]
  );

  // The active scene for the board view – merge session updates into an empty scene if null
  const activeBoardScene = useMemo(
    () => sessionScene ?? EMPTY_SCENE,
    [sessionScene]
  );

  useEffect(() => {
    const sceneId = activeBoardScene.id;
    setBoardViewSceneId(sceneId);
    setBoardViewState(loadGridViewState(sceneId, playerViewRole));
  }, [activeBoardScene.id, playerViewRole]);

  useEffect(() => {
    if (!activeBoardScene.id || boardViewSceneId !== activeBoardScene.id) return undefined;
    const timeoutId = window.setTimeout(() => {
      saveGridViewState(activeBoardScene.id, boardViewState, playerViewRole);
    }, 150);
    return () => window.clearTimeout(timeoutId);
  }, [activeBoardScene.id, boardViewSceneId, boardViewState, playerViewRole]);

  const backgroundStyle = {
    minHeight: '100vh',
    background: `linear-gradient(180deg, ${theme.backgroundSoft}, ${theme.background})`,
  };

  // Whether to show the full board view
  const showBoardView = !!connectedToken && useBoardView && gameplaySyncEnabled;

  return (
    <>
      <ToastContainer
        position='bottom-center'
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme='colored'
      />

      {/* Board View – shown when gameplay sync is enabled and player opted in */}
      {showBoardView ? (
        <div className="player_board_view">
          {/* Top bar */}
          <div className="player_board_topbar">
            <div className="player_board_topbar_left">
              <img src={dfLogo} alt="Dimension Factory" className="player_board_logo" />
              <span className="player_board_session_label">
                {connectedToken}
              </span>
              {sessionScene?.name && (
                <span className="player_board_scene_label">
                  &middot; {sessionScene.name}
                </span>
              )}
              {!playerControlsEnabled && (
                <span className="player_board_controls_badge">View Only</span>
              )}
            </div>
            <div className="player_board_topbar_right">
              <Button
                icon={Dice6}
                description="Dice Tray"
                type={showBoardDicePanel ? 'cinnabar' : 'onyx'}
                onPress={() => setShowBoardDicePanel((prev) => !prev)}
              />
              <Button
                icon={CircleQuestionMark}
                description={showTokenNames ? 'Hide Token Names' : 'Show Token Names'}
                type={showTokenNames ? 'cinnabar' : 'onyx'}
                onPress={() => setShowTokenNames((prev) => !prev)}
              />
              <Button
                icon={Ruler}
                description={measuringTokenId ? 'Disable Measure' : 'Measure From Selected Token'}
                type={measuringTokenId ? 'cinnabar' : 'onyx'}
                disabled={!selectedTokenId || !playerControlsEnabled}
                onPress={() => toggleMeasureForToken(selectedTokenId)}
              />
              <Button
                icon={Dices}
                description="Dice History"
                type="onyx"
                onPress={() => setShowDiceHistoryModal(true)}
              />
              {playerWorldMap && (playerWorldMap.layers || []).length > 0 && (
                <Button
                  icon={Map}
                  description="Map"
                  type="onyx"
                  onPress={() => setShowWorldMapModal(true)}
                />
              )}
              {playerLibrary && (playerLibrary.books || []).length > 0 && (
                <Button
                  icon={Library}
                  description="Resources"
                  type="onyx"
                  onPress={() => setShowLibraryModal(true)}
                />
              )}
              {/* 
              <Button
                icon={MonitorPlay}
                description="Switch to Classic View"
                type="onyx"
                onPress={() => setUseBoardView(false)}
              />*/}
              <Button
                label="Disconnect"
                type="black-white"
                onPress={disconnect}
              />
              <Button
                icon={LogOut}
                description="Logout"
                type="onyx"
                onPress={handleLogout}
              />
            </div>
          </div>

          <div className="player_board_content">
            {showBoardDicePanel && !isMobileBoard && (
              <aside className="player_board_dice_panel" aria-label="Dice Tray Panel">
                <div className="player_board_dice_header">Dice Tray</div>

                <div className="player_row player_row_duality player_board_dice_duality">
                  <DualityModeButton
                    mode={dualityMode}
                    onModeChange={setDualityMode}
                    disabled={loading}
                  />
                  <Button
                    label="Advantage"
                    icon={Plus}
                    type={dualityAdv ? 'cinnabar' : 'onyx'}
                    disabled={loading}
                    onPress={() => toggleDuality('adv')}
                  />
                  <Button
                    label="Disadvantage"
                    icon={Minus}
                    type={dualityDis ? 'cinnabar' : 'onyx'}
                    disabled={loading}
                    onPress={() => toggleDuality('dis')}
                  />

                  <div className="player_mods">
                    <span>+</span>
                    <Input
                      className="player_mod_input"
                      value={modPlus}
                      onChange={(e) => setModPlus(e.target.value)}
                      placeholder="0"
                      disabled={loading}
                    />
                    <span style={{ marginLeft: 4 }}>-</span>
                    <Input
                      className="player_mod_input"
                      value={modMinus}
                      onChange={(e) => setModMinus(e.target.value)}
                      placeholder="0"
                      disabled={loading}
                    />
                  </div>

                  <div style={{ marginLeft: 'auto' }}>
                    <DualityRollButton
                      mode={dualityMode}
                      disabled={loading}
                      onRoll={() => sendCommand(buildDualityCommand({
                        dualityMode,
                        dualityAdv,
                        dualityDis,
                        modPlus,
                        modMinus,
                      }))}
                    />
                  </div>
                </div>

                <div className="player_custom_block player_board_dice_custom">
                  {custom.map((value, idx) => (
                    <div className="player_row" key={`board-dice-${idx}`}>
                      <Input
                        className="player_input"
                        placeholder={idx === 0 ? 'e.g. 1d20, 2d6 + 1d10, 2d10 + 4' : ''}
                        value={value}
                        onChange={(e) => {
                          const next = [...custom];
                          next[idx] = e.target.value;
                          setCustom(next);
                        }}
                        disabled={loading}
                      />
                      <Button
                        icon={resolveDieIcon(value, Dice6)}
                        type="black-white"
                        description={loading || !value.trim() ? '' : `Roll ${value}`}
                        disabled={loading || !value.trim()}
                        onPress={() => sendCommand(value)}
                      />
                    </div>
                  ))}
                </div>

                <div className="player_board_dice_actions">
                  <Button
                    label="Clear Dice"
                    type="onyx"
                    disabled={loading}
                    onPress={() => sendCommand('Clear')}
                  />
                </div>
              </aside>
            )}

            {/* Grid board area */}
            <div
              className={`player_board_grid_area${boardZoom > 1 ? ' player_board_grid_area_zoomed' : ''}`}
            >
              <div className="player_board_zoom_controls">
                <ZoomRepeatButton
                  icon={ZoomOut}
                  description="Zoom Out"
                  type="onyx"
                  disabled={boardZoom <= 1}
                  onPress={handleBoardZoomOut}
                />
                <span className="player_board_zoom_value">{Math.round(boardZoom * 100)}%</span>
                <ZoomRepeatButton
                  icon={ZoomIn}
                  description="Zoom In"
                  type="onyx"
                  disabled={boardZoom >= 5}
                  onPress={handleBoardZoomIn}
                />
                <Button
                  icon={RotateCcw}
                  description="Reset View"
                  type="onyx"
                  onPress={handleBoardZoomReset}
                />
              </div>

              <div
                className="player_board_panel_transform"
              >
                <div className="manager_panel manager_panel_dark player_board_panel">
                  <div className="player_board_interaction_surface">
                    <GridBoard
                      scene={activeBoardScene}
                      onDragToken={playerControlsEnabled ? handlePlayerDragToken : undefined}
                      dragVisualScale={boardZoom}
                      boardOverlayScale={boardZoom}
                      pingZoomScale={boardZoom}
                      onTokenDoubleClick={playerControlsEnabled ? toggleMeasureForToken : undefined}
                      rangeSourceId={measuringTokenId}
                      readOnly={!playerControlsEnabled}
                      selectedTokenId={selectedTokenId}
                      onSelectToken={playerControlsEnabled ? setSelectedTokenId : undefined}
                      showNames={showTokenNames}
                      pings={playerPings}
                      onPingDoubleClick={handleBoardPing}
                      gridCols={boardGridConfig.cols}
                      gridRows={boardGridConfig.rows}
                      gridType={boardGridConfig.type}
                      viewState={boardViewState}
                      onViewStateChange={(view) => setBoardViewState(normalizeGridViewState(view))}
                      cameraInteractive
                      cameraWheelZoom
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {error && <div className="player_board_error">{error}</div>}
        </div>
      ) : (
        /* Classic View */
        <div
          className="page_container orchestrator_player_page"
          style={backgroundStyle}
        >
          <div className="player_header">
            <div className="player_title">Orchestrator For Players</div>
            <div className="player_subtitle">
              Signed in as {username || 'unknown'}
            </div>
          </div>

          {!connectedToken ? (
            <div className="player_connect_card">
              <div className="player_card_title">Connect to a Session</div>
              <Input
                className="player_input"
                placeholder="Enter 12-character session token"
                value={sessionToken}
                onChange={(e) => setSessionToken(e.target.value)}
                disabled={loading}
              />
              {error && <div className="player_error">{error}</div>}
              <div
                style={{
                  marginTop: 10,
                  display: 'flex',
                  gap: 10,
                  flexWrap: 'wrap',
                  justifyContent: 'center',
                }}
              >
                <Button
                  label={loading ? 'Connecting...' : 'Connect'}
                  type="cinnabar"
                  disabled={loading || !sessionToken.trim()}
                  onPress={connect}
                />
                <Button
                  icon={LogOut}
                  description="Logout"
                  type="onyx"
                  disabled={loading}
                  onPress={handleLogout}
                />
              </div>
            </div>
          ) : (
            <div className="player_roll_card">
              <div className="player_card_title player_card_title_row">
                <span>Session: {connectedToken}</span>
                <Button icon={Dices} description="Dice History" onPress={() => setShowDiceHistoryModal(true)} />
                {playerWorldMap && (
                  <Button
                    icon={Map}
                    description="Map"
                    type="onyx"
                    onPress={() => setShowWorldMapModal(true)}
                  />
                )}
                {playerLibrary && (
                  <Button
                    icon={Library}
                    description="Resources"
                    type="onyx"
                    onPress={() => setShowLibraryModal(true)}
                  />
                )}
                {/* 
                {gameplaySyncEnabled && (
                  <Button
                    icon={Gamepad2}
                    description="Switch to Board View"
                    type="cinnabar"
                    onPress={() => setUseBoardView(true)}
                  />
                )} */}
              </div>

              <div className="player_row player_row_duality">
                <DualityModeButton
                  mode={dualityMode}
                  onModeChange={setDualityMode}
                  disabled={loading}
                />
                <Button
                  label="Advantage"
                  icon={Plus}
                  type={dualityAdv ? 'cinnabar' : 'onyx'}
                  disabled={loading}
                  onPress={() => toggleDuality('adv')}
                />
                <Button
                  label="Disadvantage"
                  icon={Minus}
                  type={dualityDis ? 'cinnabar' : 'onyx'}
                  disabled={loading}
                  onPress={() => toggleDuality('dis')}
                />

                <div className="player_mods">
                  <span>+</span>
                  <Input
                    className="player_mod_input"
                    value={modPlus}
                    onChange={(e) => setModPlus(e.target.value)}
                    placeholder="0"
                    disabled={loading}
                  />
                  <span style={{ marginLeft: 4 }}>-</span>
                  <Input
                    className="player_mod_input"
                    value={modMinus}
                    onChange={(e) => setModMinus(e.target.value)}
                    placeholder="0"
                    disabled={loading}
                  />
                </div>

                <div style={{ marginLeft: 'auto' }}>
                  <DualityRollButton
                    mode={dualityMode}
                    disabled={loading}
                    onRoll={() => sendCommand(buildDualityCommand({
                      dualityMode,
                      dualityAdv,
                      dualityDis,
                      modPlus,
                      modMinus,
                    }))}
                  />
                </div>
              </div>

              <div className="player_custom_block">
                {custom.map((value, idx) => (
                  <div className="player_row" key={idx}>
                    <Input
                      className="player_input"
                      placeholder={idx === 0 ? 'e.g. 1d20, 2d6 + 1d10, 2d10 + 4' : ''}
                      value={value}
                      onChange={(e) => {
                        const next = [...custom];
                        next[idx] = e.target.value;
                        setCustom(next);
                      }}
                      disabled={loading}
                    />
                    <Button
                      icon={resolveDieIcon(value, Dice6)}
                      type="black-white"
                      description={loading || !value.trim() ? '' : `Roll ${value}`}
                      disabled={loading || !value.trim()}
                      onPress={() => sendCommand(value)}
                    />
                  </div>
                ))}
              </div>

              <div className="player_controls_block">
                <div className="player_controls_header">
                  <div className="player_card_title">Player Controls</div>
                </div>
                <div className="player_controls_hint player_controls_hint_left">
                  Fallback: Manage the player controls by moving them using the following buttons.
                </div>
                {!playerControlsEnabled && (
                  <div className="player_controls_hint">
                    GM has disabled player controls.
                  </div>
                )}

                {playerControlsEnabled && (
                  <div className="player_controls_body">
                    <div className="player_tokens_list">
                      {playerTokens.length === 0 && (
                        <div className="player_controls_hint">
                          No visible tokens are available.
                        </div>
                      )}
                      {playerTokens.map((token) => (
                        <button
                          key={token.id}
                          type="button"
                          className={`player_token_chip${selectedTokenId === token.id ? ' player_token_chip_selected' : ''}`}
                          onClick={() => handleTokenChipTap(token.id)}
                          onDoubleClick={() => toggleMeasureForToken(token.id)}
                          disabled={loading}
                        >
                          {token.name || 'Token'}
                        </button>
                      ))}
                    </div>

                    <div className="player_controls_buttons">
                      <div className="player_nudge_controls">
                        <Button
                          icon={ChevronUp}
                          type="cinnabar"
                          description="Move Up"
                          disabled={loading || !selectedTokenId}
                          onPress={() => sendMoveSelected(0, -1)}
                        />
                        <div className="player_nudge_row">
                          <Button
                            icon={ChevronLeft}
                            type="cinnabar"
                            description="Move Left"
                            disabled={loading || !selectedTokenId}
                            onPress={() => sendMoveSelected(-1, 0)}
                          />
                          <Button
                            icon={ChevronRight}
                            type="cinnabar"
                            description="Move Right"
                            disabled={loading || !selectedTokenId}
                            onPress={() => sendMoveSelected(1, 0)}
                          />
                        </div>
                        <div>
                          <Button
                            icon={ChevronDown}
                            type="cinnabar"
                            description="Move Down"
                            disabled={loading || !selectedTokenId}
                            onPress={() => sendMoveSelected(0, 1)}
                          />
                        </div>
                      </div>
                      <div className="player_measure_toggle_slot">
                        <Button
                          icon={Ruler}
                          type={selectedTokenId && measuringTokenId === selectedTokenId ? 'onyx' : 'cinnabar'}
                          description={selectedTokenId && measuringTokenId === selectedTokenId ? 'Disable Measure' : 'Measure From Selected Token'}
                          disabled={loading || !selectedTokenId}
                          onPress={() => toggleMeasureForToken(selectedTokenId)}
                        />
                      </div>
                    </div>
                  </div>
                )}
                {playerControlsEnabled && (
                  <div className='player_battle_effect_block'>
                    <div className='player_battle_effect_title'>Battle Effect</div>
                    <div className='player_controls_hint player_controls_hint_left'>
                      Apply a shaped effect on the GM map, centred on the selected token.
                    </div>
                    <div className='player_battle_effect_row'>
                      <label className='player_battle_effect_label'>
                        <span>Shape</span>
                        <Select
                          className='player_battle_effect_select'
                          value={battleEffectShape}
                          onChange={(e) => setBattleEffectShape(e.target.value)}
                          disabled={loading}
                        >
                          <option value='line_h'>Line (H, both ways)</option>
                          <option value='line_v'>Line (V, both ways)</option>
                          <option value='line_n'>Line North</option>
                          <option value='line_s'>Line South</option>
                          <option value='line_e'>Line East</option>
                          <option value='line_w'>Line West</option>
                          <option value='line_ne'>Line NE</option>
                          <option value='line_nw'>Line NW</option>
                          <option value='line_se'>Line SE</option>
                          <option value='line_sw'>Line SW</option>
                          <option value='cone_right'>Cone Right</option>
                          <option value='cone_left'>Cone Left</option>
                          <option value='cone_up'>Cone Up</option>
                          <option value='cone_down'>Cone Down</option>
                          <option value='circle'>Circle</option>
                          <option value='square'>Square</option>
                        </Select>
                      </label>
                      <label className='player_battle_effect_label'>
                        <span>Size</span>
                        <Input
                          className='player_battle_effect_size'
                          type='number'
                          min='1'
                          max='10'
                          step='1'
                          value={battleEffectSize}
                          onChange={(e) => setBattleEffectSize(Number(e.target.value))}
                          disabled={loading}
                        />
                      </label>
                      <label className='player_battle_effect_label'>
                        <span>Color</span>
                        <Input
                          type='color'
                          value={battleEffectColor}
                          onChange={(e) => setBattleEffectColor(e.target.value)}
                          disabled={loading}
                        />
                      </label>
                    </div>
                    <div className='player_battle_effect_row'>
                      <label className='player_battle_effect_label player_battle_effect_label_wide'>
                        <span>Opacity ({Math.round(battleEffectOpacity * 100)}%)</span>
                        <Input
                          type='range'
                          min='0.1'
                          max='0.8'
                          step='0.05'
                          value={battleEffectOpacity}
                          onChange={(e) => setBattleEffectOpacity(Number(e.target.value))}
                          disabled={loading}
                        />
                      </label>
                    </div>
                    <div className='player_battle_effect_apply'>
                      <Button
                        label='Apply Effect'
                        type='cinnabar'
                        disabled={loading || !selectedTokenId}
                        onPress={sendBattleEffectCommand}
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Library section */}
              {playerLibrary && (playerLibrary.books || []).length > 0 && (
                <div className='player_controls_block'>
                  <div className='player_controls_header'>
                    <div className='player_card_title'>Resources</div>
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 12,
                      marginTop: 10,
                    }}
                  >
                    {(playerLibrary.books || []).map((book) => (
                      <button
                        key={book.id}
                        type='button'
                        onClick={() => {
                          setLibraryInitialBookId(book.id);
                          setShowLibraryModal(true);
                        }}
                        style={{
                          cursor: 'pointer',
                          width: 76,
                          background: 'none',
                          border: 'none',
                          padding: 0,
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 5,
                          alignItems: 'center',
                        }}
                      >
                        <div
                          style={{
                            width: 76,
                            height: 104,
                            borderRadius: 3,
                            overflow: 'hidden',
                            background: 'rgba(255,255,255,0.08)',
                            border: '1px solid rgba(255,255,255,0.15)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          {book.coverUrl ? (
                            <img
                              src={book.coverUrl}
                              alt={book.title}
                              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            />
                          ) : (
                            <BookOpen size={22} style={{ color: 'rgba(255,255,255,0.3)' }} />
                          )}
                        </div>
                        <span
                          style={{
                            fontSize: 11,
                            color: '#fff',
                            textAlign: 'center',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            maxWidth: 76,
                            display: 'block',
                          }}
                        >
                          {book.title}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="player_row player_row_actions">
                <Button
                  label="Clear Dice"
                  type="onyx"
                  disabled={loading}
                  onPress={() => sendCommand('Clear')}
                />
                <Button
                  label="Disconnect"
                  type="black-white"
                  disabled={loading}
                  onPress={disconnect}
                />
              </div>

              {error && <div className="player_error">{error}</div>}
            </div>
          )}
        </div>
      )}

      {/* Shared modals */}
      {showDiceHistoryModal && (
        <DiceHistoryModal
          visible={showDiceHistoryModal}
          rolls={sessionRolls}
          mode={theme?.mode || 'dark'}
          onClose={() => setShowDiceHistoryModal(false)}
        />
      )}
      {showWorldMapModal && playerWorldMap && (
        <WorldMapModal
          visible={showWorldMapModal}
          worldMap={playerWorldMap}
          isGm={false}
          mode={theme?.mode || 'dark'}
          onClose={() => setShowWorldMapModal(false)}
        />
      )}
      {showLibraryModal && playerLibrary && (
        <LibraryModal
          visible={showLibraryModal}
          library={playerLibrary}
          isGm={false}
          mode={theme?.mode || 'dark'}
          onClose={() => { setShowLibraryModal(false); setLibraryInitialBookId(null); }}
          initialBookId={libraryInitialBookId}
        />
      )}
      {showBoardView && isMobileBoard && showBoardDicePanel && (
        <GmDiceModal
          visible={showBoardDicePanel}
          mode={theme?.mode || 'dark'}
          dualityMode={dualityMode}
          onDualityModeChange={setDualityMode}
          onClose={() => setShowBoardDicePanel(false)}
          onRoll={sendCommand}
        />
      )}
      {isOnBreak && <BreakScreen />}
    </>
  );
}
